import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CalendarClock,
  Calendar,
  Clock,
  Shield,
  AlertTriangle,
  Lock,
  Info,
} from '@/lib/icons';

import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ButtonSpinner } from '@/components/feedback';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  getInstallmentSettings,
  updateInstallmentSettings,
} from '@/features/installment-settings/api';
import type {
  TenantInstallmentSettings,
  InstallmentFrequency,
} from '@/features/installment-settings/installment-settings';

export const Route = createFileRoute('/_layout/settings/installment')({
  component: InstallmentSettingsPage,
});

/* ── Constants ─────────────────────────────────── */

const FREQUENCY_CONFIG: Record<
  InstallmentFrequency,
  { label: string; badgeClass: string; gradient: string }
> = {
  daily: {
    label: 'Daily',
    badgeClass: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
    gradient: 'from-blue-500 to-blue-600',
  },
  weekly: {
    label: 'Weekly',
    badgeClass: 'bg-violet-500/15 text-violet-600 border-violet-500/30',
    gradient: 'from-violet-500 to-violet-600',
  },
  monthly: {
    label: 'Monthly',
    badgeClass: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    gradient: 'from-emerald-500 to-emerald-600',
  },
};

/* ── Form schema ───────────────────────────────── */

const EscalationFormSchema = z.object({
  graceDays: z.coerce.number().min(0),
  warningDays: z.coerce.number().min(0),
  partialLockDays: z.coerce.number().min(0),
  fullLockDays: z.coerce.number().min(0),
  missedCountDefault: z.coerce.number().min(1),
});

type EscalationFormValues = z.infer<typeof EscalationFormSchema>;

/* ── Frequency Card Component ──────────────────── */

function FrequencyCard({
  settings,
  frequency,
}: Readonly<{
  settings: TenantInstallmentSettings;
  frequency: InstallmentFrequency;
}>) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const config = FREQUENCY_CONFIG[frequency];

  const form = useForm<EscalationFormValues>({
    resolver: zodResolver(EscalationFormSchema),
    defaultValues: {
      graceDays: settings.graceDays,
      warningDays: settings.warningDays,
      partialLockDays: settings.partialLockDays,
      fullLockDays: settings.fullLockDays,
      missedCountDefault: settings.missedCountDefault,
    },
  });

  useEffect(() => {
    form.reset({
      graceDays: settings.graceDays,
      warningDays: settings.warningDays,
      partialLockDays: settings.partialLockDays,
      fullLockDays: settings.fullLockDays,
      missedCountDefault: settings.missedCountDefault,
    });
  }, [settings, form]);

  const mutation = useMutation({
    mutationFn: (values: EscalationFormValues) => {
      const payload: Record<string, unknown> = {
        graceDays: values.graceDays,
        warningDays: values.warningDays,
        partialLockDays: values.partialLockDays,
        fullLockDays: values.fullLockDays,
        missedCountDefault: values.missedCountDefault,
      };
      return updateInstallmentSettings(frequency, payload);
    },
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['installment-settings'] });
        toast({
          title: 'Success',
          description: `${config.label} settings updated successfully`,
        });
      } else {
        toast({
          title: 'Error',
          description: result.errors?.[0] ?? 'Failed to update settings',
          variant: 'destructive',
        });
      }
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (values: EscalationFormValues) => {
    mutation.mutate(values);
  };

  return (
    <Card className="shadow-md border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-xl bg-gradient-to-br ${config.gradient} text-white shadow-lg`}
          >
            {frequency === 'daily' ? (
              <Clock className="h-4 w-4" />
            ) : frequency === 'weekly' ? (
              <Calendar className="h-4 w-4" />
            ) : (
              <CalendarClock className="h-4 w-4" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{config.label}</CardTitle>
              <Badge className={config.badgeClass}>{frequency}</Badge>
            </div>
            <CardDescription className="text-xs mt-0.5">
              {frequency === 'daily'
                ? 'Daily payment schedule'
                : frequency === 'weekly'
                  ? 'Weekly payment schedule'
                  : 'Monthly payment schedule'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Payment Schedule Section */}
            <div className="space-y-3">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                Payment Schedule
              </p>

              <div className="flex items-center gap-2 rounded-lg bg-blue-500/10 px-3 py-2.5">
                <Info className="h-4 w-4 text-blue-600 shrink-0" />
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  {frequency === 'daily'
                    ? 'One installment every calendar day from the contract start date.'
                    : frequency === 'weekly'
                      ? 'One installment every 7 days from the contract start date.'
                      : 'One installment every 30 days from the contract start date.'}
                </p>
              </div>
            </div>

            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

            {/* Escalation Thresholds Section */}
            <div className="space-y-3">
              <p className="text-sm font-semibold flex items-center gap-2">
                <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                Escalation Thresholds
              </p>

              <FormField
                control={form.control}
                name="graceDays"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm font-medium flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      Grace Period (days)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        className="font-medium"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Days after due date before escalation begins
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="warningDays"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />
                      Warning (days)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        className="font-medium"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Days in warning state before partial lock
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partialLockDays"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm font-medium flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                      Partial Lock (days)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        className="font-medium"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Days in partial lock before full lock
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fullLockDays"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm font-medium flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                      Full Lock (days)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        className="font-medium"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Days in full lock before default
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="missedCountDefault"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />
                      Missed to Default (count)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        className="font-medium"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Number of missed payments before contract defaults
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isDirty || mutation.isPending}
            >
              {mutation.isPending && <ButtonSpinner className="mr-2" />}
              Save {config.label} Settings
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

/* ── Main Page ─────────────────────────────────── */

function InstallmentSettingsPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['installment-settings'],
    queryFn: getInstallmentSettings,
  });

  const settingsList = data?.value;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-80 mt-2" />
          </div>
          <Skeleton className="h-10 w-10 rounded-xl" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="shadow-md border-0">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48 mt-1" />
              </CardHeader>
              <CardContent className="pt-2 space-y-5">
                {[1, 2, 3, 4, 5].map((j) => (
                  <div key={j} className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Installment Settings
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Configure payment frequencies, escalation thresholds, and default
              payment days
            </p>
          </div>
        </div>
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title="Failed to load installment settings"
        />
      </div>
    );
  }

  const frequencyOrder: InstallmentFrequency[] = ['daily', 'weekly', 'monthly'];
  const settingsByFrequency = new Map(
    settingsList?.map((s) => [s.frequency, s])
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Installment Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure payment frequencies, escalation thresholds, and default
            payment days
          </p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-lg">
          <CalendarClock className="h-5 w-5" />
        </div>
      </div>

      {/* Frequency Cards */}
      <div className="grid gap-6 lg:grid-cols-3">
        {frequencyOrder.map((freq) => {
          const s = settingsByFrequency.get(freq);
          if (!s) return null;
          return <FrequencyCard key={freq} settings={s} frequency={freq} />;
        })}
      </div>
    </div>
  );
}
